import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchSiteVideos, deleteSiteVideo, type Site, type SiteVideo, type SiteVideoUsage } from '@/services/siteApi';

// "Your videos" on /my-site: every video file that came in with an upload or a
// move, now playing through Mux, with how much of the included GB is used.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const linkBtn: React.CSSProperties = { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 13, fontWeight: 500, color: MUTED, textDecoration: 'underline', cursor: 'pointer' };
const pill = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', background: bg, color: fg, fontWeight: 600, fontSize: 12, padding: '3px 9px', borderRadius: 999, flexShrink: 0 });

const size = (b: number) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : b >= 1024 ** 2 ? `${Math.round(b / 1024 ** 2)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const mins = (s: number | null) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');

const SiteVideosPanel: React.FC<{ site: Site }> = ({ site }) => {
  const [videos, setVideos] = useState<SiteVideo[] | null>(null);
  const [usage, setUsage] = useState<SiteVideoUsage | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = () => fetchSiteVideos(site.id).then((r) => { setVideos(r.videos); setUsage(r.usage); }).catch(() => setVideos([]));
  useEffect(() => { load(); }, [site.id]);
  // While Mux is still processing something, check back.
  useEffect(() => {
    if (!videos?.some((v) => v.status === 'preparing')) return;
    const t = window.setTimeout(load, 15000);
    return () => window.clearTimeout(t);
  }, [videos]);
  if (!videos || !videos.length || !usage) return null;
  const over = usage.extraGb > 0;
  return (
    <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
      <h2 style={{ margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', color: INK }}>Your videos</h2>
      <p style={small}>These play through a streaming player on your pages, so they start fast and work on phones. Using {size(usage.bytes)} of the {size(usage.includedBytes)} included with hosting.{over ? ` That’s ${usage.extraGb} GB over, which adds $${(usage.extraGb * usage.extraGbCents / 100).toFixed(0)} a month to your hosting.` : ''}</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {videos.map((v) => (
          <li key={v.id} className="flex items-center" style={{ gap: 12, padding: '10px 0', borderTop: '1px solid var(--hj-hairline-soft)' }}>
            <div className="flex flex-col" style={{ minWidth: 0, flex: 1 }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title || v.path}</span>
              <span style={{ ...small, fontSize: 12 }}>{v.path} · {size(v.bytes)}{v.durationSec ? ` · ${mins(v.durationSec)}` : ''}</span>
            </div>
            {v.status === 'ready' ? <span style={pill('#e3f1d3', '#2f5d1a')}>Playing</span> : v.status === 'preparing' ? <span style={pill('var(--hj-yellow-tint)', INK)}><Loader2 className="w-3 h-3 animate-spin" style={{ marginRight: 4 }} />Processing</span> : <span style={pill('var(--hj-pink-tint)', 'var(--hj-pink-ink)')}>Failed</span>}
            <button type="button" disabled={busy === v.id} style={linkBtn} onClick={async () => { setBusy(v.id); try { const r = await deleteSiteVideo(site.id, v.id); setUsage(r.usage); setVideos(videos.filter((x) => x.id !== v.id)); } catch { /* keep the row */ } setBusy(null); }}>Remove</button>
          </li>
        ))}
      </ul>
      <p style={{ ...small, fontSize: 12 }}>Removing a video here frees the space; the spot on your page where it played goes dark until you upload a version without it.</p>
    </section>
  );
};

export default SiteVideosPanel;
